/**
 * Lightweight memory monitoring for Reflux.
 * Focuses on leak detection - features Redux DevTools cannot provide.
 *
 * Enable via:
 * - localStorage.setItem('siRefluxMonitoring', 'true')
 * - localStorage.setItem('siRefluxMonitoring', JSON.stringify({ enabled: true, stackTraces: true }))
 * - window.__REFLUX_MONITORING__.setEnabled(true)
 *
 * @module reflux/monitoring
 */

/** localStorage key for monitoring configuration */
const STORAGE_KEY = 'siRefluxMonitoring';

// Subscription tracking
let subId = 0;
const subscriptions = new Map();
let configLoaded = false;

// Pending stack for component-level capture
// Set by hooks before calling listen(), consumed by trackSub()
let pendingStack = null;

// Action log (lightweight - name + timestamp only, no payload)
const actionLog = [];
const MAX_ACTION_LOG = 50;

// Configuration
const config = {
  enabled: false,
  maxListenersWarning: 50,
  trackActions: false,    // Actions are noise - off by default
  captureStacks: false    // Stack traces for debugging - opt-in
};

/**
 * Maps localStorage config keys to internal config keys with type coercion.
 * Format: [storageKey, configKey, coercionFn]
 */
const CONFIG_MAPPING = [
  ['enabled', 'enabled', Boolean],
  ['stackTraces', 'captureStacks', Boolean],
  ['trackActions', 'trackActions', Boolean],
  ['maxListenersWarning', 'maxListenersWarning', (v) => Number(v) || 50]
];

/**
 * Apply parsed config object to internal config.
 * @param {Object} parsed - Parsed JSON config object
 */
const applyParsedConfig = (parsed) => {
  if (typeof parsed !== 'object' || parsed === null) return;

  for (const [storageKey, configKey, coerce] of CONFIG_MAPPING) {
    if (parsed[storageKey] !== undefined) {
      config[configKey] = coerce(parsed[storageKey]);
    }
  }
};

/**
 * Load configuration from localStorage.
 * Supports both simple 'true' value and JSON object config.
 *
 * @example
 * // Simple enable
 * localStorage.siRefluxMonitoring = 'true'
 *
 * // Full config
 * localStorage.siRefluxMonitoring = JSON.stringify({
 *   enabled: true,
 *   stackTraces: true,
 *   trackActions: false,
 *   maxListenersWarning: 100
 * })
 */
const loadConfigFromStorage = () => {
  if (configLoaded) return;
  if (typeof localStorage === 'undefined') return;

  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return;

  // Simple 'true' value - just enable
  if (stored === 'true') {
    config.enabled = true;
    configLoaded = true;
    return;
  }

  // Try to parse as JSON
  try {
    applyParsedConfig(JSON.parse(stored));
  } catch (error) {
    // Invalid JSON - treat as disabled, warn user about config error
    console.warn(`[Reflux Monitoring] Invalid JSON in localStorage.${STORAGE_KEY}:`, error.message);
  }

  configLoaded = true;
};

/**
 * Enable or disable memory monitoring.
 *
 * @param {boolean} enabled - Whether to enable monitoring
 */
export const setEnabled = (enabled) => {
  config.enabled = enabled;
};

/**
 * Check if memory monitoring is enabled.
 * Can be enabled via config or localStorage.siRefluxMonitoring.
 *
 * @returns {boolean} True if monitoring is enabled
 */
export const isEnabled = () => {
  loadConfigFromStorage();
  return config.enabled;
};

/**
 * Enable or disable action tracking.
 * Actions are not tracked by default since stores listening to actions is normal.
 *
 * @param {boolean} enabled - Whether to track action subscriptions
 */
export const setTrackActions = (enabled) => {
  config.trackActions = enabled;
};

/**
 * Enable or disable stack trace capture.
 * When enabled, captures the call stack when subscriptions are created.
 * Useful for identifying where leaked subscriptions originated.
 *
 * @param {boolean} enabled - Whether to capture stack traces
 */
export const setStackTraces = (enabled) => {
  config.captureStacks = enabled;
};

/**
 * Enable monitoring and persist to localStorage.
 * Page reload required for full subscription tracking from boot.
 */
export const enableAndPersist = () => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    enabled: true,
    stackTraces: true,
    trackActions: true
  }));
};

/**
 * Disable monitoring and remove from localStorage.
 */
export const disableAndPersist = () => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Check if monitoring was enabled at page boot (via localStorage).
 * Used by DevTools to determine if full subscription data is available.
 *
 * @returns {boolean} True if monitoring was enabled at boot
 */
export const wasEnabledAtBoot = () => {
  return localStorage.getItem(STORAGE_KEY) !== null;
};

// ============================================================================
// localStorage Config Helpers (DRY pattern for get/set operations)
// ============================================================================

/**
 * Get a value from the localStorage config object.
 * Handles JSON parsing and provides fallback on error.
 *
 * @param {string} key - Config key to retrieve
 * @param {*} defaultValue - Value to return if key doesn't exist or on error
 * @returns {*} Config value or defaultValue
 */
const getConfigValue = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaultValue;
    const parsed = JSON.parse(stored);
    return parsed[key] ?? defaultValue;
  } catch {
    return defaultValue;
  }
};

/**
 * Set a value in the localStorage config object.
 * Preserves existing config values. Does nothing if monitoring is disabled.
 *
 * @param {string} key - Config key to set
 * @param {*} value - Value to store
 */
const setConfigValue = (key, value) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return; // Don't create storage if monitoring is disabled
    const parsed = JSON.parse(stored);
    parsed[key] = value;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Get pinned stores from localStorage config.
 * Used by DevTools to persist which stores the user has pinned.
 *
 * @returns {string[]} Array of pinned store names
 */
export const getPinnedStores = () => getConfigValue('pinnedStores', []);

/**
 * Save pinned stores to localStorage config.
 * Preserves existing config values (enabled, stackTraces, etc.).
 *
 * @param {string[]} pinnedStores - Array of store names to pin
 */
export const setPinnedStores = (pinnedStores) => setConfigValue('pinnedStores', pinnedStores);

/**
 * Get actions panel expanded state from localStorage config.
 * Used by DevTools to persist panel visibility.
 *
 * @returns {boolean} Whether the actions panel is expanded
 */
export const getActionsExpanded = () => getConfigValue('actionsExpanded', false);

/**
 * Save actions panel expanded state to localStorage config.
 * Preserves existing config values.
 *
 * @param {boolean} expanded - Whether the actions panel is expanded
 */
export const setActionsExpanded = (expanded) => setConfigValue('actionsExpanded', expanded);

/**
 * Get actions panel width from localStorage config.
 * Used by DevTools to persist resizable panel width.
 *
 * @returns {number} Panel width in pixels (default: 300)
 */
export const getActionsPanelWidth = () => getConfigValue('actionsPanelWidth', 300);

/**
 * Save actions panel width to localStorage config.
 * Preserves existing config values.
 *
 * @param {number} width - Panel width in pixels
 */
export const setActionsPanelWidth = (width) => setConfigValue('actionsPanelWidth', width);

/**
 * Set a pre-captured stack trace to be used for the next subscription.
 * This allows hooks to capture the stack during render (when the calling
 * component is on the stack) rather than during useEffect (when only
 * React internals are on the stack).
 *
 * The stack is consumed by the next trackSub() call and cleared.
 *
 * @param {string} stack - Pre-captured Error().stack string
 */
export const setNextSubscriptionStack = (stack) => {
  pendingStack = stack;
};

// ============================================================================
// Action Log (T126-T128)
// ============================================================================

/**
 * Log an action invocation with lazy stack capture.
 * Stack is captured immediately (cheap) but only parsed when accessed (expensive).
 * Called from createAction when monitoring is enabled.
 *
 * @param {string} actionName - Name of the action that was triggered
 * @param {Function} [actionFn] - The action function (used as constructorOpt to filter frames)
 */
export const logAction = (actionName, actionFn) => {
  const entry = { name: actionName, time: Date.now() };

  // V8 only: capture stack lazily (~0.001ms)
  // Stack string is generated only when .stack is accessed
  if (typeof Error.captureStackTrace === 'function' && actionFn) {
    Error.captureStackTrace(entry, actionFn);
  }

  actionLog.push(entry);
  if (actionLog.length > MAX_ACTION_LOG) {
    actionLog.shift();
  }
};

/**
 * Extract source location from a log entry's captured stack.
 * Only parses when called (deferred cost ~0.1ms per entry).
 *
 * @param {Object} entry - Action log entry with .stack property
 * @returns {string|null} Source location like "Modal.jsx:45" or null
 */
const getSourceLocation = (entry) => {
  if (!entry.stack) return null;

  const lines = entry.stack.split('\n');
  // First line is often just the action name, skip it and find actual stack frames
  for (const line of lines) {
    if (shouldSkipFrame(line)) continue;
    const parsed = parseStackFrame(line);
    // Skip lines that don't parse to a valid frame (e.g., action name on first line)
    if (parsed) return parsed;
  }
  return null;
};

/**
 * Get recent action log with source locations.
 * Parses stack traces on retrieval (cost paid only when DevTools requests data).
 *
 * @returns {Array<{name: string, time: number, source: string|null}>} Array of action log entries
 */
export const getActionLog = () =>
  actionLog.map(entry => ({
    name: entry.name,
    time: entry.time,
    source: getSourceLocation(entry)
  }));

/**
 * Clear the action log.
 */
export const clearActionLog = () => {
  actionLog.length = 0;
};

/**
 * Generic filenames that need parent directory for context.
 */
const GENERIC_FILENAMES = [
  'index.jsx', 'index.js', 'index.ts', 'index.tsx',
  'utils.js', 'utils.ts', 'helpers.js', 'helpers.ts',
  'constants.js', 'constants.ts', 'types.ts'
];

/**
 * Extract file:line from a single stack frame.
 * For generic filenames like index.jsx, includes parent directory for context.
 *
 * @param {string} line - Stack trace line
 * @returns {string|null} file:line or null if not parseable
 */
export const parseStackFrame = (line) => {
  // Extract file:line from various stack formats
  // Chrome/Node: "at functionName (file:line:col)" or "at file:line:col"
  // Firefox: "functionName@file:line:col"
  const match = line.match(/(?:at\s+)?(?:\S+\s+\()?([^()]+):(\d+):\d+\)?/) ||
                line.match(/@([^@]+):(\d+):\d+/);

  if (match) {
    let filePath = match[1];
    const lineNum = match[2];

    // Strip Vite cache-busting query string first (before splitting path)
    filePath = filePath.split('?')[0];

    // Split path into segments
    const segments = filePath.split(/[/\\]/);
    const fileName = segments.pop();

    // For generic filenames, include parent directory for context
    if (GENERIC_FILENAMES.includes(fileName) && segments.length > 0) {
      const parentDir = segments.pop();
      return `${parentDir}/${fileName}:${lineNum}`;
    }

    return `${fileName}:${lineNum}`;
  }
  return null;
};

/**
 * Patterns to skip when filtering stack frames.
 * Includes internal files, hooks, and build artifacts.
 */
const SKIP_FRAME_PATTERNS = [
  '/reflux/',       // Internal reflux files (Unix paths)
  '\\reflux\\',     // Internal reflux files (Windows paths)
  'useStoreState',  // Hook itself - we want the calling component
  'useStoreData',   // Hook (in case stack wasn't captured during render)
  'connectToStores', // HOC (class-based, can't use hooks for stack capture)
  'chunk-',         // Vite bundled chunks (e.g., chunk-6KWORUEV.js)
  'node_modules',   // Third-party dependencies
  // Note: removed '?v=' - Vite adds this to ALL source files in dev mode
  // Action stack filtering
  'createAction',   // Action factory internals
  'triggerAsync',   // Publisher async trigger
  'Publisher'       // Publisher class methods
];

/**
 * Check if a stack frame should be skipped (not useful for debugging).
 *
 * @param {string} line - Stack trace line
 * @returns {boolean} True if frame should be skipped
 */
export const shouldSkipFrame = (line) => SKIP_FRAME_PATTERNS.some(p => line.includes(p));

/**
 * Extract multiple file:line references from a stack trace.
 * Shows 2-3 frames to identify both the hook AND the component calling it.
 * Filters out bundled chunks and node_modules to show only meaningful source files.
 *
 * @param {string} stack - Error stack trace
 * @returns {string} Formatted stack location(s), e.g., "Modal.jsx:45 ← Parent.jsx:12"
 */
const extractStackLocation = (stack) => {
  if (!stack) return null; // No stack captured - return null to distinguish from parse failure

  const lines = stack.split('\n');
  const frames = [];
  const maxFrames = 3;

  // Skip first 3 lines (Error, trackSub, listen), then search deeper for valid frames
  for (let i = 3; i < lines.length && frames.length < maxFrames; i++) {
    const line = lines[i];
    // Skip unhelpful frames (reflux internals, chunks, node_modules, hooks)
    if (shouldSkipFrame(line)) continue;

    const frame = parseStackFrame(line);
    if (frame) {
      frames.push(frame);
    }
  }

  if (frames.length === 0) return '(filtered)'; // Stack exists but no useful frames after filtering

  // Join frames with arrow: "Component.jsx:45 ← Parent.jsx:12"
  return frames.join(' ← ');
};

/**
 * Count subscriptions for a publisher and warn if threshold exceeded.
 *
 * @param {string} publisherName - Name of the publisher to check
 */
const warnIfTooManyListeners = (publisherName) => {
  const count = [...subscriptions.values()].filter(s => s.publisher === publisherName).length;
  if (count > config.maxListenersWarning) {
    console.warn(`[Reflux Memory] "${publisherName}" has ${count} listeners - possible leak?`);
  }
};

/**
 * Capture stack trace for a subscription if enabled.
 *
 * @param {Object} sub - Subscription object to add stack info to
 */
const captureStackIfEnabled = (sub) => {
  if (!config.captureStacks) return;

  // Use pre-captured stack from hook if available (better component-level info)
  // Otherwise fall back to capturing here (works for store-to-store subscriptions)
  const stack = pendingStack || new Error().stack;
  pendingStack = null; // Clear after use
  sub.stack = stack;
  sub.location = extractStackLocation(stack);
};

/**
 * Track a new subscription. Called from publisher.listen().
 *
 * @param {string} publisherName - Name of the publisher being subscribed to
 * @param {string} [type] - Type of publisher: 'action', 'store', or null
 * @returns {number|null} Subscription ID for untracking, or null if disabled
 */
export const trackSub = (publisherName, type = null) => {
  if (!isEnabled()) return null;
  if (type === 'action' && !config.trackActions) return null;

  const id = ++subId;
  const sub = { id, publisher: publisherName, type, created: Date.now() };

  captureStackIfEnabled(sub);
  subscriptions.set(id, sub);
  warnIfTooManyListeners(publisherName);

  return id;
};

/**
 * Untrack a subscription. Called from unlisten().
 *
 * @param {number|null} id - Subscription ID to untrack
 */
export const untrackSub = (id) => {
  if (id) subscriptions.delete(id);
};

/**
 * Find potentially leaked subscriptions (older than maxAgeMs).
 *
 * @param {number} [maxAgeMs=300000] - Maximum age in milliseconds (default 5 minutes)
 * @returns {Array} Array of subscription objects that may be leaks
 */
export const getPotentialLeaks = (maxAgeMs = 300000) => {
  const now = Date.now();
  return [...subscriptions.values()].filter(s => now - s.created > maxAgeMs);
};

/**
 * Get all active subscriptions.
 *
 * @returns {Array} Array of all subscription objects
 */
export const getActiveSubscriptions = () => {
  return [...subscriptions.values()];
};

/**
 * Clear all tracked subscriptions.
 * Useful for testing or resetting state.
 */
export const clearTracking = () => {
  subscriptions.clear();
  subId = 0;
};

/**
 * Group subscriptions by publisher, calculating count and oldest age.
 *
 * @param {Array} subs - Array of subscription objects
 * @param {number} now - Current timestamp for age calculation
 * @returns {Object} Map of publisher name to {count, oldestAge}
 */
const groupSubscriptionsByPublisher = (subs, now) => {
  const grouped = {};
  for (const sub of subs) {
    const age = now - sub.created;
    if (!grouped[sub.publisher]) {
      grouped[sub.publisher] = { count: 0, oldestAge: 0 };
    }
    grouped[sub.publisher].count++;
    grouped[sub.publisher].oldestAge = Math.max(grouped[sub.publisher].oldestAge, age);
  }
  return grouped;
};

/**
 * Convert grouped data to sorted array format.
 *
 * @param {Object} grouped - Map of publisher name to {count, oldestAge}
 * @returns {Array} Array of {name, count, oldestAge} sorted by count descending
 */
const toSortedStoreArray = (grouped) =>
  Object.entries(grouped)
    .map(([name, data]) => ({ name, count: data.count, oldestAge: data.oldestAge }))
    .sort((a, b) => b.count - a.count);

/**
 * Get store subscription statistics for UI consumption.
 * Returns structured data without console logging.
 *
 * @returns {Object} Statistics object with stores array and total
 * @returns {Array} stores - Array of {name, count, oldestAge} sorted by count
 * @returns {number} total - Total number of store subscriptions
 *
 * @example
 * const stats = __REFLUX_MONITORING__.getStoreStats();
 * // { stores: [{name: 'SelectionStore', count: 11, oldestAge: 26000}, ...], total: 61 }
 */
export const getStoreStats = () => {
  const now = Date.now();
  const subs = [...subscriptions.values()].filter(s => s.type === 'store');
  const grouped = groupSubscriptionsByPublisher(subs, now);
  const stores = toSortedStoreArray(grouped);

  return { stores, total: subs.length };
};

/**
 * Get individual subscribers for a specific store.
 * Returns structured data for detail view UI.
 *
 * @param {string} storeName - Name of the store to get subscribers for
 * @returns {Array} Array of {id, age, location} sorted by age (newest first)
 *
 * @example
 * const subs = __REFLUX_MONITORING__.getStoreSubscribers('SelectionStore');
 * // [{id: 42, age: 3000, location: 'useStoreState.jsx:30 ← Modal.jsx:45'}, ...]
 */
export const getStoreSubscribers = (storeName) => {
  const now = Date.now();
  const subs = [...subscriptions.values()]
    .filter(s => s.type === 'store' && s.publisher === storeName);

  return subs
    .map(s => ({
      id: s.id,
      age: now - s.created,
      location: s.location || null,
      rawStack: s.stack || null // Include raw stack for debugging/verification
    }))
    .sort((a, b) => a.age - b.age); // Newest first (smallest age)
};

/**
 * Set the warning threshold for listeners per publisher.
 *
 * @param {number} threshold - Maximum listeners before warning
 */
export const setMaxListenersWarning = (threshold) => {
  config.maxListenersWarning = threshold;
};

/**
 * Reset all module-level state for test isolation.
 * Call this in test setup/teardown to ensure clean state between tests.
 *
 * @example
 * beforeEach(() => {
 *   __REFLUX_MONITORING__.resetConfig();
 *   __REFLUX_MONITORING__.clearTracking();
 * });
 */
export const resetConfig = () => {
  configLoaded = false;
  config.enabled = false;
  config.maxListenersWarning = 50;
  config.trackActions = false;
  config.captureStacks = false;
};

// Expose globally for debugging (always available, but tracking only when enabled)
if (typeof window !== 'undefined') {
  window.__REFLUX_MONITORING__ = {
    // Core
    setEnabled,
    isEnabled,
    clearTracking,
    setMaxListenersWarning,
    resetConfig,

    // Store stats (for DevTools UI)
    getStoreStats,
    getStoreSubscribers,
    getActiveSubscriptions,

    // Leak detection
    getPotentialLeaks,

    // Action log
    getActionLog,
    clearActionLog,

    // Configuration
    setStackTraces,
    setTrackActions,

    // UI state persistence
    getPinnedStores,
    setPinnedStores,
    getActionsExpanded,
    setActionsExpanded,
    getActionsPanelWidth,
    setActionsPanelWidth,

    // Hook integration (for component-level stack traces)
    setNextSubscriptionStack
  };
}
